import React from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import {
    TextField,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Button,
    Typography,
    Link,
    DialogActions,
    CircularProgress
} from "@mui/material";

const RegistrationSchema = Yup.object().shape({
    fullName: Yup.string()
        .required("Full Name is required")
        .min(2, "Full Name is too short")
        .max(50, "Full Name is too long"),
    email: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password is too short")
        .max(50, "Password is too long")
        .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character.") // Special character check
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter.") // Uppercase letter check
        .matches(/[a-z]/, "Password must contain at least one lowercase letter.") // Lowercase letter check
        .matches(/\d/, "Password must contain at least one digit."), // Digit check
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], "Passwords must match")
        .required("Confirm Password is required"),
    acceptTC: Yup.boolean()
        .oneOf([true], "You must accept the terms and conditions")
});

const RegistrationForm = ({ isRegistering, onLogin, onSubmit, onClose }) => (
    <Formik
        initialValues={{ fullName: "", email: "", password: "", confirmPassword: "", acceptTC: false }}
        validationSchema={RegistrationSchema}
        onSubmit={onSubmit}
    >
        {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
                {/* Full Name */}
                <TextField
                    margin="dense"
                    id="fullName"
                    name="fullName"
                    label="Full Name"
                    type="text"
                    fullWidth
                    value={values.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.fullName && Boolean(errors.fullName)}
                    helperText={touched.fullName && errors.fullName}
                    disabled={isRegistering}
                />

                {/* Email */}
                <TextField
                    margin="dense"
                    id="email"
                    name="email"
                    label="Email"
                    type="email"
                    fullWidth
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    disabled={isRegistering}
                />

                {/* Password */}
                <TextField
                    margin="dense"
                    id="password"
                    name="password"
                    label="Password"
                    type="password"
                    autoComplete="password"
                    fullWidth
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    disabled={isRegistering}
                />

                {/* Confirm Password */}
                <TextField
                    margin="dense"
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    autoComplete="confirmPassword"
                    fullWidth
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    disabled={isRegistering}
                />

                {/* Terms and Conditions */}
                <FormControlLabel
                    control={
                        <Checkbox
                            id="acceptTC"
                            name="acceptTC"
                            checked={values.acceptTC}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isRegistering}
                            color="primary"
                        />
                    }
                    label="I agree to the Terms and Conditions"
                />

                {touched.acceptTC && Boolean(errors) && (
                    <FormHelperText error>{errors.acceptTC}</FormHelperText>
                )}

                <Typography variant="body2" sx={{ mt: 2 }}>
                    {isRegistering ? (
                        <span style={{ color: "gray", cursor: "default" }}>Already have an account? Login</span>
                    ) : (
                        <Link color="primary" sx={{ cursor: "pointer", color: "primary.dark" }} onClick={onLogin}>Already have an account? Login</Link>
                        // <Link component="button" onClick={onLogin}>Already have an account? Login</Link>
                    )}
                </Typography>

                <DialogActions>
                    <Button
                        type="submit"
                        sx={{
                            color: "primary.dark"
                        }}
                        disabled={isRegistering || !values.acceptTC}
                        startIcon={isRegistering ? <CircularProgress sx={{ color: "primary.dark" }} /> : null}
                    >
                        {isRegistering ? "Registering..." : "Register"}
                    </Button>

                    <Button onClick={onClose}
                        sx={{
                            color: "warning.dark"
                        }}
                        disabled={isRegistering}
                    >
                        Cancel
                    </Button>

                </DialogActions>

            </Form>
        )}
    </Formik>
);

export default RegistrationForm;